// User and role administration (D51, D52) over `/api/admin`, and accepting an invitation.
// Each call needs the permission its route names; pages hide what the user cannot do.
import { queryOptions } from "@tanstack/react-query";

import { api, type Schemas, type User } from "../api/client";
import { ApiError, unwrap } from "../api/queries";

export type Role = Schemas["RoleDto"];
export type PermissionGroup = Schemas["PermissionGroupDto"];
export type Invitation = Schemas["InvitationDto"];

export const usersQuery = () => queryOptions({ queryKey: ["users"], queryFn: () => unwrap(api.GET("/api/admin/users")) });

export const rolesQuery = () => queryOptions({ queryKey: ["roles"], queryFn: () => unwrap(api.GET("/api/admin/roles")) });

export const permissionsQuery = () =>
  queryOptions({ queryKey: ["permissions"], queryFn: () => unwrap(api.GET("/api/admin/permissions")), staleTime: Infinity });

export interface NewUser {
  email: string;
  display_name: string;
  role_id?: string;
  /** A temporary password; omit to invite with a link instead. */
  password?: string;
}

export function addUser(user: NewUser): Promise<User> {
  return unwrap(api.POST("/api/admin/users", { body: user }));
}

const userPath = (userId: string) => ({ params: { path: { user_id: userId } } });

export function inviteUser(userId: string): Promise<Invitation> {
  return unwrap(api.POST("/api/admin/users/{user_id}/invitation", userPath(userId)));
}

export function setUserPassword(userId: string, password: string): Promise<User> {
  return unwrap(api.PUT("/api/admin/users/{user_id}/password", { ...userPath(userId), body: { password } }));
}

export function setUserDisabled(userId: string, disabled: boolean): Promise<User> {
  const params = userPath(userId);
  return unwrap(
    disabled ? api.POST("/api/admin/users/{user_id}/disable", params) : api.POST("/api/admin/users/{user_id}/enable", params),
  );
}

export function setUserRole(userId: string, roleId: string): Promise<User> {
  return unwrap(api.PUT("/api/admin/users/{user_id}/role", { ...userPath(userId), body: { role_id: roleId } }));
}

export function createRole(name: string, description: string): Promise<Role> {
  return unwrap(api.POST("/api/admin/roles", { body: { name, description, color: "" } }));
}

/** 204 on success, so not through `unwrap` (which wants a body). */
export async function deleteRole(roleId: string): Promise<void> {
  const { error, response } = await api.DELETE("/api/admin/roles/{role_id}", { params: { path: { role_id: roleId } } });
  if (!response.ok) throw new ApiError(response.status, error);
}

export function setRolePermissions(roleId: string, permissions: string[]): Promise<Role> {
  return unwrap(api.PUT("/api/admin/roles/{role_id}/permissions", { params: { path: { role_id: roleId } }, body: { permissions } }));
}

/** Public: sets the invited user's password; they then sign in. */
export function acceptInvitation(token: string, password: string): Promise<Schemas["InvitationAccepted"]> {
  return unwrap(api.POST("/api/invitations/{token}/accept", { params: { path: { token } }, body: { password } }));
}
